<!--
  Bereich anlegen (TIM-73).

  Bewusst schlank: Art, Name, Zeitraum. Farbe und Status kommen vom
  Hauptprojekt, weil ein Bereich zu ihm gehoert - wer sie abweichend will,
  aendert sie hinterher in der Projektmaske.

  "Bereich" statt "Unterprojekt" in der Oberflaeche: eine Unterkunft ist kein
  Projekt. Technisch bleibt es eines (parent_id), und genau deshalb erbt es
  alles, was ein Projekt kann.
-->
<template>
  <!-- Wie der Personenfilter am rechten Rand, volle Hoehe (Hoffi, 18.09.). -->
  <Sheet :open="props.open" @update:open="(offen) => { if (!offen) emit('cancel') }">
    <SheetContent side="right" class="w-[28rem] max-w-full h-full p-0 flex flex-col">
    <div class="sp-dialog" role="dialog" :aria-label="titel">
      <div class="sp-head">
        <h2>{{ titel }}</h2>
        <!-- Kein eigenes Kreuz: das Fenster bringt seins mit. -->
      </div>

      <div class="sp-body">
        <!-- Mehrere auf einmal, wie die Abschnitte mit + und - (TIM-81/2). -->
        <div v-for="(zeile, i) in zeilen" :key="zeile.key" class="sp-row">
          <label class="sp-field">
            <span v-if="i === 0">Art</span>
            <select v-model="zeile.kategorie">
              <option v-for="(label, key) in CATEGORIES" :key="key" :value="key">{{ label }}</option>
            </select>
          </label>

          <label class="sp-field sp-name">
            <span v-if="i === 0">Name</span>
            <input :ref="(el) => nameRefs[i] = el" v-model="zeile.name" type="text"
                   :placeholder="platzhalter(zeile.kategorie)" @keyup.enter="speichern" />
          </label>

          <button v-if="zeilen.length > 1" type="button" class="sp-remove" title="Zeile entfernen"
                  @click="zeilen.splice(i, 1)">−</button>
        </div>

        <button type="button" class="sp-add" @click="zeileHinzufuegen">+ weiterer Bereich</button>

        <p v-if="fehler" class="sp-error">{{ fehler }}</p>
        <p v-else class="sp-hint">
          Zeitraum, Farbe und Status übernimmt jeder Bereich von
          {{ props.project?.name }}<template v-if="zeitraum"> ({{ zeitraum }})</template>.
          Ein Leistungsbereich erbt auch dessen Pausen.
        </p>
      </div>

      <div class="sp-foot">
        <Button variant="outline" @click="emit('cancel')">Abbrechen</Button>
        <Button class="bg-green-500 hover:bg-green-600 text-white"
                :disabled="laeuft" @click="speichern">
          {{ laeuft ? 'Wird angelegt …' : (zeilen.length > 1 ? `${zeilen.length} anlegen` : 'Anlegen') }}
        </Button>
      </div>
    </div>
    </SheetContent>
  </Sheet>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { toGerman } from '@/utils/dateUtils'
import { CATEGORIES, sendRequest } from './planner.js'

const props = defineProps({
  open: { type: Boolean, required: true },
  project: { type: Object, default: null },
})

const emit = defineEmits(['cancel', 'created'])

const zeilen = ref([])
const fehler = ref('')
const laeuft = ref(false)
const nameRefs = ref([])
let naechsterKey = 0

function neueZeile(kategorie = Object.keys(CATEGORIES)[0]) {
  return { key: naechsterKey++, kategorie, name: '' }
}

const titel = computed(() => `Neuer Bereich in ${props.project?.name ?? ''}`)
function platzhalter(kategorie) {
  return kategorie === 'accommodation' ? 'z. B. Hotel Nord' : 'z. B. Rohbau'
}

const zeitraum = computed(() => {
  const p = props.project
  return p?.start && p?.end ? `${toGerman(p.start)} – ${toGerman(p.end)}` : ''
})

watch(() => props.open, (offen) => {
  if (!offen) return
  zeilen.value = [neueZeile()]
  fehler.value = ''
  laeuft.value = false
  nextTick(() => nameRefs.value[0]?.focus())
})

// Die neue Zeile uebernimmt die Art der letzten - wer mehrere anlegt, legt
// meist mehrere derselben Art an.
function zeileHinzufuegen() {
  const letzte = zeilen.value[zeilen.value.length - 1]
  zeilen.value.push(neueZeile(letzte?.kategorie))
  const i = zeilen.value.length - 1
  nextTick(() => nameRefs.value[i]?.focus())
}

function anlegen(zeile) {
  return new Promise((resolve, reject) => {
    sendRequest('/projects.json', {
      project: {
        name: zeile.name.trim(),
        description: `Bereich von ${props.project.name}`,
        status: props.project.status,
        color: props.project.color,
        // Ein Bereich laeuft, solange sein Hauptprojekt laeuft. Das Modell zieht
        // den Zeitraum ohnehin nach; was hier steht, ist nur die erste Fassung.
        // Fachlich koennte ein Bereich einen eigenen Zeitraum haben - technisch
        // ist er ja ein Projekt -, aber bisher braucht das niemand, und ein Feld
        // weniger heisst eine Entscheidung weniger beim Anlegen.
        start: props.project.start,
        end: props.project.end,
        parent_id: props.project.id,
        category: zeile.kategorie,
      },
    }, resolve, reject)
  })
}

// Nacheinander statt gleichzeitig: scheitert einer, ist klar, welche schon
// stehen. Die bleiben angelegt und verschwinden aus der Liste, stehen bleiben
// nur die, die noch fehlen - ein zweiter Klick legt sonst doppelt an.
async function speichern() {
  if (laeuft.value) return
  fehler.value = ''

  // Leere Zusatzzeilen stillschweigend weglassen, nur ganz leer ist ein Fehler.
  const zuAnlegen = zeilen.value.filter(z => z.name.trim())
  if (zuAnlegen.length === 0) {
    fehler.value = 'Bitte einen Namen angeben.'
    return
  }
  laeuft.value = true
  for (const zeile of zuAnlegen) {
    try {
      const neu = await anlegen(zeile)
      emit('created', neu)
      zeilen.value = zeilen.value.filter(z => z !== zeile)
    } catch (antwort) {
      laeuft.value = false
      // Der Server meldet Validierungsfehler als {base: [...]} oder je Feld.
      const texte = antwort && typeof antwort === 'object' ? Object.values(antwort).flat() : []
      const grund = texte.length ? texte.join(' ') : 'konnte nicht angelegt werden.'
      fehler.value = `„${zeile.name.trim()}“: ${grund}`
      return
    }
  }
  laeuft.value = false
  emit('cancel')
}
</script>

<style scoped>
.sp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.sp-dialog {
  background: #fff;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.sp-head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.sp-close {
  background: none;
  border: 0;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  padding: 2px;
}
.sp-close:hover { color: #111827; }

.sp-body {
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  overflow-y: auto;
}

.sp-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
}

.sp-field > span {
  color: #4b5563;
}

.sp-field select,
.sp-field input[type="text"] {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.35rem 0.5rem;
  font-size: 0.9rem;
  background: #fff;
}

.sp-field select:focus,
.sp-field input[type="text"]:focus {
  outline: 2px solid #86b7dc;
  outline-offset: 0;
  border-color: #86b7dc;
}

.sp-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}

.sp-name { flex: 1; }

.sp-remove {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.3rem 0.55rem;
  background: #fff;
  color: #4b5563;
  line-height: 1;
  cursor: pointer;
}
.sp-remove:hover { background: #f3f4f6; }

.sp-add {
  align-self: flex-start;
  background: none;
  border: 0;
  padding: 0;
  font-size: 0.85rem;
  color: #4b5563;
  cursor: pointer;
}
.sp-add:hover { text-decoration: underline; }

.sp-hint {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
}

.sp-error {
  margin: 0;
  font-size: 0.85rem;
  color: #9c4221;
}

.sp-foot {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.9rem 1.25rem;
  border-top: 1px solid #e5e7eb;
}
</style>
